import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Button, Input, Checkbox, HStack, VStack, Image } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import Calendar from '../components/Calendar';
import SocialMediaAndPhotosStep from '../components/SocialMediaAndPhotosStep';
import CompletionScreen from '../components/CompletionScreen';
import BackButton from '../components/BackButton';
import PhoneInput from '../components/PhoneInput';
import LocationSelector from '../components/LocationSelector';
import { uploadImageToOracleBucket, generateTemporaryPassword, generateUniqueId } from '../utils';
import { MAX_PHOTO_SIZE_BYTES, MAX_RETRY_ATTEMPTS, STEP_TRANSITION_DELAY_MS, MIN_PHONE_NUMBER_LENGTH, MIN_PHOTOS_REQUIRED } from '../constants';
import { ROUTES, API_ROUTES } from '../constants';

const InfluencerSignUpWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4; // 4 actual steps, completion screen is separate

  // Step 1 - Phone Number
  const [phoneNumber, setPhoneNumber] = useState('');

  // Step 2 - Personal Details (previously Step 1)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [selectedBirthday, setSelectedBirthday] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedGender, setSelectedGender] = useState<'male' | 'female' | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const birthdayInputRef = useRef<HTMLInputElement>(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [allergies, setAllergies] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Get email from navigation state (passed from LoginPage)
  useEffect(() => {
    const state = location.state as { email?: string } | null;
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location]);

  // Step 3 - Location Details (previously Step 2)
  const [selectedCountryLocation, setSelectedCountryLocation] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // Step 4 - Profile Details (previously Step 5)
  const [instagramHandle, setInstagramHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [youtubeHandle, setYoutubeHandle] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Phone number validation function
  const isValidPhoneNumber = (phone: string) => {
    const phoneRegex = /^[0-9\s]+$/;
    return phoneRegex.test(phone) && phone.replace(/\s/g, '').length >= MIN_PHONE_NUMBER_LENGTH;
  };

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if form is valid for current step
  const getStepValidation = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!(phoneNumber && isValidPhoneNumber(phoneNumber) && !isLoading);
      case 2:
        return !!(firstName && lastName && email && validateEmail(email) && selectedBirthday && selectedGender && agreedToTerms && !isLoading && !emailError);
      case 3:
        return !!(selectedCountryLocation && selectedCity && !isLoading);
      case 4:
        return !!(uploadedPhotos.length >= MIN_PHOTOS_REQUIRED && !isLoading);
      default:
        return false;
    }
  };

  const isFormValid = getStepValidation();

  const handleDateSelect = (date: Date) => {
    setSelectedBirthday(date);
    setBirthday(date.toLocaleDateString());
    setShowCalendar(false);
  };

  const handleBirthdayFocus = () => {
    setShowCalendar(true);
  };

  const handleBirthdayBlur = () => {
    // Don't close calendar on blur - let it stay open for better UX
    // Calendar will close when date is selected or user clicks outside
  };

  // Calculate calendar position when it should be shown
  useEffect(() => {
    if (showCalendar && birthdayInputRef.current) {
      const updatePosition = () => {
        if (birthdayInputRef.current) {
          const inputRect = birthdayInputRef.current.getBoundingClientRect();
          const containerRect = birthdayInputRef.current.closest('.base-container')?.getBoundingClientRect();
          
          if (containerRect) {
            // Position relative to the base container
            // Position above the input: input top - calendar height - margin
            const top = inputRect.top - containerRect.top - 344; // 336px calendar height + 8px margin
            // Center horizontally relative to input
            const left = inputRect.left - containerRect.left + (inputRect.width / 2) - 171.5; // Center the calendar (343px / 2)
            setCalendarPosition({ top, left });
          }
        }
      };
      
      updatePosition();
      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showCalendar, currentStep]);

  // Handle click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside calendar and not on the input
      if (
        calendarRef.current && 
        !calendarRef.current.contains(target) &&
        !birthdayInputRef.current?.contains(target) &&
        !target.closest('[data-calendar="true"]')
      ) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);


  const handleNext = async () => {
    if (!isFormValid) return;

    setIsLoading(true);

    try {
      if (currentStep < totalSteps) {
        await new Promise(resolve => setTimeout(resolve, STEP_TRANSITION_DELAY_MS));
        setCurrentStep(currentStep + 1);
      } else {
        // Final step (step 4) - submit to API
        if (!config?.apiUrl) {
          throw new Error('API URL not configured');
        }

        // Format birthday as ISO string
        const birthdayString = selectedBirthday ? selectedBirthday.toISOString().split('T')[0] : '';

        // Validate email format
        if (!email || !validateEmail(email)) {
          throw new Error('Invalid email address');
        }

        if (!config?.oracleBucket) {
          throw new Error('Oracle bucket configuration not found');
        }

        // Retry logic for ID conflicts
        const maxRetries = MAX_RETRY_ATTEMPTS;
        let retryCount = 0;
        let signupSuccess = false;

        while (retryCount < maxRetries && !signupSuccess) {
          try {
            // Generate unique ID on frontend
            const influencerId = generateUniqueId('inf');
            console.log(`Attempt ${retryCount + 1}: Generated influencer ID: ${influencerId}`);

            // Upload photos first using the generated ID
            let photoUrls: string[] = [];
            if (uploadedPhotos.length > 0) {
              try {
                photoUrls = await Promise.all(
                  uploadedPhotos.map(file => uploadImageToOracleBucket(file, influencerId, config.oracleBucket!))
                );
                console.log('Photos uploaded successfully:', photoUrls.length, 'photos');
              } catch (photoError: any) {
                console.error('Error uploading photos:', photoError);
                throw new Error(`Failed to upload photos: ${photoError.message || 'Unknown error'}`);
              }
            }

            // Prepare API payload with generated ID and photo URLs
            const payload = {
              id: influencerId, // Include generated ID in request
              phoneNumber: phoneNumber.replace(/\s/g, ''),
              email: email.trim(),
              firstName,
              lastName,
              birthday: birthdayString,
              gender: selectedGender,
              allergies: allergies,
              // Normalize country and city to lowercase
              country: selectedCountryLocation?.toLowerCase(),
              city: selectedCity?.toLowerCase(),
              password: generateTemporaryPassword(), // Temporary password - user will need to reset
              instagramHandle: instagramHandle,
              tiktokHandle: tiktokHandle,
              youtubeHandle: youtubeHandle,
              photos: photoUrls // Include uploaded photo URLs in signup
            };

            // Submit to API to create user with generated ID and photos
            const response = await axios.post(`${config.apiUrl}${API_ROUTES.AUTH_INFLUENCER_SIGNUP}`, payload);

            if (response.status === 201) {
              // Success - move to completion screen
              signupSuccess = true;
              setCurrentStep(5);
            } else {
              throw new Error(response.data?.error || 'Failed to create account');
            }
          } catch (error: any) {
            // Check if error is due to ID conflict
            if (error.response?.status === 409 || 
                error.response?.data?.error?.includes('already exists') ||
                error.response?.data?.error?.includes('ID already exists')) {
              retryCount++;
              if (retryCount >= maxRetries) {
                throw new Error('Failed to generate unique ID after multiple attempts. Please try again.');
              }
              console.log(`ID conflict detected, retrying with new ID (attempt ${retryCount + 1}/${maxRetries})...`);
              // Continue loop to retry with new ID
            } else {
              // Other errors should be thrown immediately
              throw error;
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error saving form:', error);
      // Show error to user (you might want to add a toast/alert here)
      alert(error.response?.data?.error || error.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      // On step 1, navigate back to landing page
      navigate(ROUTES.HOME);
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountryLocation(country);
    // Clear city selection when country changes
    setSelectedCity('');
  };


  return (
    <Box
      className="base-container"
      position="relative"
    >

      {/* Main Content */}
      <Box
        className="wizard-main-content"
      >
        {/* Progress indicator - positioned at top right */}
        {currentStep <= totalSteps && (
          <Box
            position="absolute"
            top="148px"
            right="20px"
            width="25px"
            height="32px"
            zIndex={10}
          >
            <Text
              className="subtitle-text"
            >
              {currentStep}/{totalSteps}
            </Text>
          </Box>
        )}

        {/* Back Arrow Button - Hide on completion screen */}
        {currentStep !== 5 && (
          <Box
            position="absolute"
            top="59px"
            left="22px"
            zIndex={10}
          >
            <BackButton onClick={handleBack} />
          </Box>
        )}

        {/* Title - "Sign up as Influencer" */}
        <Box
          position="absolute"
          top="61px"
          left="73px"
          width="292px"
          height="32px"
          zIndex={10}
        >
            <Text
              className="subtitle-text"
            >
              {currentStep === 5 ? "" : "Sign up as Influencer"}
            </Text>
        </Box>

        {/* Subtitle - changes based on step */}
        {currentStep <= totalSteps && (
          <Box
            position="absolute"
            top="147px"
            left="22px"
            width="300px"
            height="64px"
            zIndex={10}
          >
            <Text
              className="title-text"
            >
              {currentStep === 1 ? "Phone number" :
               currentStep === 2 ? "Tell us about yourself" :
               currentStep === 3 ? "Awesome! Time to add your location" :
               "Let's add some more information right now!"}
            </Text>
          </Box>
        )}

        {/* Scrollable Step Content Container */}
        <Box className="wizard-step-content">
          {/* Step 1: Phone Number */}
          {currentStep === 1 && (
          <>
            {/* Phone Number Input */}
            <Box
              position="absolute"
              top="0px"
              left="22px"
              width="330px"
              height="44px"
            >
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </Box>

          </>
        )}

        {/* Step 2: Personal Details */}
        {currentStep === 2 && (
          <>
            {/* Personal Details Form */}
            <Box              
              top="0px"              
              className="form-container"
            >

          <VStack spacing="16px" align="stretch">
            {/* First Name */}
            <Box>
              <Input
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="input-in-form"
              />
            </Box>

            {/* Last Name */}
            <Box>
              <Input
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="input-in-form"
              />
            </Box>

            {/* Email */}
            <Box>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  const emailValue = e.target.value;
                  setEmail(emailValue);
                  // Clear error when user starts typing
                  if (emailError) {
                    setEmailError('');
                  }
                  // Validate email format
                  if (emailValue && !validateEmail(emailValue)) {
                    setEmailError('Please enter a valid email address');
                  } else {
                    setEmailError('');
                  }
                }}
                className="input-in-form"
              />
              {emailError && (
                <Text
                  className="error-message"
                >
                  {emailError}
                </Text>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Birthday Form */}
        <Box
          top="170px"          
          className="form-container"
        >
          <Box position="relative">
            <Input
              ref={birthdayInputRef}
              placeholder="Birthday"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              onFocus={handleBirthdayFocus}
              onBlur={handleBirthdayBlur}
              className="input-in-form"
            />
          </Box>
        </Box>

        {/* Gender Selection */}
        <Box
          top="280px"          
          className="form-container"
        >
          <Text
            className="regular-text"
          >
            We request your gender to suggest events suited to your profile
          </Text>

          <HStack spacing="16px" marginTop="16px" alignItems="center" justifyContent="center">
            {/* Female Option */}
            <Box
              className="sex-option"
              borderColor={selectedGender === 'female' ? '#C6D959' : '#595C5D'}
              onClick={() => setSelectedGender('female')}
              _hover={{
                borderColor: selectedGender === 'female' ? '#C6D959' : '#90909A'
              }}
            >
              <Text
                className="regular-text"  
              >
                Female
              </Text>

              {/* Female Icon */}
              <Image
                src="/assets/images/female-img.jpg"
                alt="Female"
                className="sex-image"
              />

            </Box>

            {/* Male Option */}
            <Box
              className="sex-option"
              borderColor={selectedGender === 'male' ? '#C6D959' : '#595C5D'}
              onClick={() => setSelectedGender('male')}
              _hover={{
                borderColor: selectedGender === 'male' ? '#C6D959' : '#90909A'
              }}
            >

              <Text
                className="regular-text"
              >
                Male
              </Text>

              {/* Male Icon */}
              <Image
                src="/assets/images/male-img.jpg"
                alt="Male"
                className="sex-image"
              />

            </Box>
          </HStack>
        </Box>

        {/* Allergies Form */}
        <Box
          top="510px"          
          className="form-container"
        >
          <Box>
            
            <Input
              placeholder="Indicate what you are allergic to (optional)"
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              className="input-in-form"
            />
          </Box>
        </Box>

        {/* Terms Agreement */}
        <Box
          top="620px"          
          className="form-container"
        >
          <Text
            className="regular-text"
            marginBottom="16px"
          >
            We will be sending out important updates
          </Text>

          <HStack spacing="12px" align="flex-start">
            <Checkbox
              isChecked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              colorScheme="none"
              size="lg"
              iconColor="#120F1B"
              borderColor="#595C5D"
              borderRadius="50%"
              _checked={{
                backgroundColor: "#C6D959",
                borderColor: "#000000"
              }}
            />
            <Text
              className="static-text"
            >
              I agree
            </Text>
          </HStack>
        </Box>

          </>
        )}

        {/* Step 3: Location Selection */}
        {currentStep === 3 && (
          <>
            {/* Location Selection */}
            <Box
              top="31px"              
              className="form-container"
            >
              <LocationSelector
                selectedCountry={selectedCountryLocation}
                selectedCity={selectedCity}
                onCountryChange={handleCountryChange}
                onCityChange={setSelectedCity}
              />
            </Box>

          </>
        )}

          {/* Step 4: Profile Setup */}
          {currentStep === 4 && (
            <SocialMediaAndPhotosStep
              instagramHandle={instagramHandle}
              setInstagramHandle={setInstagramHandle}
              tiktokHandle={tiktokHandle}
              setTiktokHandle={setTiktokHandle}
              youtubeHandle={youtubeHandle}
              setYoutubeHandle={setYoutubeHandle}
              uploadedPhotos={uploadedPhotos}
              setUploadedPhotos={setUploadedPhotos}
              photoUrls={photoUrls}
              setPhotoUrls={setPhotoUrls}
              maxPhotoSize={MAX_PHOTO_SIZE_BYTES}
              isFormValid={isFormValid}
              isLoading={isLoading}
              onNext={handleNext}
              buttonText="Complete"
              topPosition={31}
              showYouTube={true}
              showWebsite={false}
              showButton={false}
            />
          )}

        {/* Step 5: Completion Screen */}
        {currentStep === 5 && (
          <CompletionScreen
            title="Our team will review your profile"
            subtitle="You will receive an email from us within the next 24 hours"            
          />
        )}
        </Box>
      </Box>

      {/* Calendar - Rendered outside scrollable container */}
      {showCalendar && currentStep === 2 && (
        <Box
          ref={calendarRef}
          position="absolute"
          top={`${calendarPosition.top}px`}
          left={`${calendarPosition.left}px`}
          zIndex={9999}
        >
          <Calendar
            selectedDate={selectedBirthday}
            onDateSelect={handleDateSelect}
            onClose={() => setShowCalendar(false)}
          />
        </Box>
      )}

      {/* Fixed Next Button - Only show for steps 1-4 */}
      {currentStep <= totalSteps && (
        <Box
          position="absolute"
          bottom="30px"
          left="22px"
          width="332px"
          height="52px"
          zIndex={10}
        >
          <Button
            className="button-big"
            onClick={handleNext}
            isLoading={isLoading}
            loadingText="Loading..."
            isDisabled={!isFormValid}
          >
            {currentStep === 4 ? "Complete" : "Next"}
          </Button>
        </Box>
      )}

      {/* Progress Bar */}
      {currentStep <= totalSteps && (
        <Box
          className="wizard-progress-bar"
        >
          <Box
            className="wizard-progress-bar-steps"
            width={`${(currentStep / totalSteps) * 100}%`}
          />
        </Box>
      )}
    </Box>
  );
};

export default InfluencerSignUpWizard;
